import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import AppShell from './components/AppShell';
import SystemConfigPage from './pages/superadmin/SystemConfigPage';
import SecurityPage from './pages/superadmin/SecurityPage';
import SmtpPage from './pages/superadmin/SmtpPage';
import AgenciesPage from './pages/admin/AgenciesPage';
import UnitsPage from './pages/admin/UnitsPage';
import PersonnelPage from './pages/admin/PersonnelPage';
import CallTypesPage from './pages/admin/CallTypesPage';
import StatusTypesPage from './pages/admin/StatusTypesPage';
import UsersPage from './pages/admin/UsersPage';
import LocationsPage from './pages/admin/LocationsPage';
import DispositionsPage from './pages/admin/DispositionsPage';
import ReportsPage from './pages/admin/ReportsPage';
import AgencyTypesPage from './pages/admin/AgencyTypesPage';
import UnitTypesPage from './pages/admin/UnitTypesPage';
import CallPrioritiesPage from './pages/admin/CallPrioritiesPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import Active911Page from './pages/admin/Active911Page';
import CaseEntryPage from './pages/admin/CaseEntryPage';
import DestinationsPage from './pages/admin/DestinationsPage';
import OperatorPositionsPage from './pages/admin/OperatorPositionsPage';
import ZonesPage from './pages/admin/ZonesPage';
import GisPage from './pages/admin/GisPage';
import StatusBoardsPage from './pages/admin/StatusBoardsPage';
import BoardPage from './pages/board/BoardPage';
import DispatchHomePage from './pages/dispatch/DispatchHomePage';
import MapPage from './pages/dispatch/MapPage';
import UnitsCallsPage from './pages/dispatch/UnitsCallsPage';
import DispatchLogPage from './pages/dispatch/DispatchLogPage';
import CallHistoryPage from './pages/dispatch/CallHistoryPage';
import ScheduledCallsPage from './pages/dispatch/ScheduledCallsPage';
import ChatPage from './pages/dispatch/ChatPage';
import WhoOnlinePage from './pages/dispatch/WhoOnlinePage';
import ChatLogPage from './pages/admin/ChatLogPage';
import MdtPage from './pages/mobile/MdtPage';
import MdtLoginPage from './pages/mobile/MdtLoginPage';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#2196f3' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
});

function AuthLoading() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <CircularProgress />
    </Box>
  );
}

function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, authReady } = useAuth();
  // Wait for the persisted token to be validated before rendering the shell,
  // so an expired token can't briefly expose protected content.
  if (!authReady) return <AuthLoading />;
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function AppRoutes() {
  const { isAuthenticated, authReady, user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated && authReady && !new URLSearchParams(window.location.search).has('auto_token')
          ? <Navigate to="/" />
          : <LoginPage />
      } />
      <Route path="/mdt" element={<MdtLoginPage />} />
      <Route path="/board/:token" element={<BoardPage />} />
      <Route path="/" element={
        <ProtectedRoute>
          <AppShell />
        </ProtectedRoute>
      }>
        <Route index element={
          <Navigate to={
            (user?.dispatch_access || user?.admin_access || user?.superadmin_access) ? '/dispatch/home' :
            user?.mobile_access ? '/mobile/mdt' : '/login'
          } />
        } />
        {/* Dispatch */}
        <Route path="dispatch/home" element={<DispatchHomePage />} />
        <Route path="dispatch/map" element={<MapPage />} />
        <Route path="dispatch/units" element={<UnitsCallsPage />} />
        <Route path="dispatch/log" element={<DispatchLogPage />} />
        <Route path="dispatch/history" element={<CallHistoryPage />} />
        <Route path="dispatch/scheduled" element={<ScheduledCallsPage />} />
        <Route path="dispatch/chat" element={<ChatPage />} />
        <Route path="dispatch/online" element={<WhoOnlinePage />} />
        {/* Admin */}
        <Route path="admin/agencies" element={<AgenciesPage />} />
        <Route path="admin/units" element={<UnitsPage />} />
        <Route path="admin/personnel" element={<PersonnelPage />} />
        <Route path="admin/call-types" element={<CallTypesPage />} />
        <Route path="admin/status-types" element={<StatusTypesPage />} />
        <Route path="admin/users" element={<UsersPage />} />
        <Route path="admin/locations" element={<LocationsPage />} />
        <Route path="admin/dispositions" element={<DispositionsPage />} />
        <Route path="admin/reports" element={<ReportsPage />} />
        <Route path="admin/agency-types" element={<AgencyTypesPage />} />
        <Route path="admin/unit-types" element={<UnitTypesPage />} />
        <Route path="admin/call-priorities" element={<CallPrioritiesPage />} />
        <Route path="admin/zones" element={<ZonesPage />} />
        <Route path="admin/gis" element={<GisPage />} />
        <Route path="admin/status-boards" element={<StatusBoardsPage />} />
        <Route path="admin/audit-log" element={<AuditLogPage />} />
        <Route path="admin/active911" element={<Active911Page />} />
        <Route path="admin/case-entry" element={<CaseEntryPage />} />
        <Route path="admin/destinations" element={<DestinationsPage />} />
        <Route path="admin/operator-positions" element={<OperatorPositionsPage />} />
        <Route path="admin/smtp" element={<SmtpPage />} />
        <Route path="admin/chat-log" element={<ChatLogPage />} />
        {/* Super-admin */}
        <Route path="superadmin/config" element={<SystemConfigPage />} />
        <Route path="superadmin/security" element={<SecurityPage />} />

        {/* Mobile */}
        <Route path="mobile/mdt" element={<MdtPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
